import React, { useEffect, useRef, useState } from "react";
import { IUserRef } from "../../models/userData.model";
import useOutsideClick from "../Assets/useOutsideClick";

interface UserSelectorSearchProps {
  users: IUserRef[];
  label: string;
  value: string | string[];
  onChange: (val: string | string[]) => void;
  multiple?: boolean;
}

const UserSelectorSearch: React.FC<UserSelectorSearchProps> = ({
  users,
  label,
  value,
  onChange,
  multiple = false,
}) => {
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);

  const selectedValues = Array.isArray(value) ? value : [value];
  const filteredUsers = users.filter(
    (u) =>
      u.username.toLowerCase().includes(search.toLowerCase()) &&
      (!multiple || !selectedValues.includes(u._id)),
  );

  useOutsideClick(containerRef, () => setOpen(false));

  useEffect(() => {
    if (!multiple && typeof value === "string") {
      const selectedUser = users.find((u) => u._id === value);
      setSearch(selectedUser ? selectedUser.username : "");
    }
  }, [value, users, multiple]);

  return (
    <div className="space-y-2" ref={containerRef}>
      <label className="block text-xs font-bold uppercase tracking-wider text-gray-500 mb-1">
        {label}
      </label>
      <div className="relative">
        <input
          type="text"
          placeholder={`Search ${label.toLowerCase()}...`}
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm transition-all duration-200 focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none text-gray-800 placeholder-gray-400 bg-white"
        />

        {open && (
          <div className="absolute z-20 mt-2 w-full max-h-56 overflow-y-auto bg-white border border-gray-100 rounded-xl shadow-xl shadow-gray-200/50 py-1.5 focus:outline-none divide-y divide-gray-50">
            {filteredUsers.length > 0 ? (
              filteredUsers.map((u) => (
                <div
                  key={u._id}
                  onClick={() => {
                    if (multiple) {
                      onChange([...selectedValues, u._id]);
                    } else {
                      onChange(u._id);
                    }
                    setSearch("");
                    setOpen(false);
                  }}
                  className="px-4 py-2.5 cursor-pointer text-gray-700 hover:bg-blue-50/60 hover:text-blue-600 text-sm transition-colors duration-150 ease-in-out font-medium"
                >
                  {u.username.capitalizeWords()}
                </div>
              ))
            ) : (
              <div className="px-4 py-4 text-center text-gray-400 text-sm italic bg-gray-50/30">
                No users found
              </div>
            )}
          </div>
        )}
      </div>

      {/* Show chips if multiple */}
      {multiple && selectedValues.length > 0 && (
        <div className="bg-gray-50/60 rounded-xl p-3 border border-gray-100/80 flex flex-wrap gap-2">
          {selectedValues.map((id) => {
            const user = users.find((u) => u._id === id);
            return (
              <div
                key={id}
                className="bg-blue-50/80 text-blue-700 px-2.5 py-1 rounded-lg flex items-center gap-1.5 text-xs font-semibold border border-blue-100/50 transition-all duration-150 hover:bg-blue-100/60"
              >
                <span>{user?.username}</span>
                <button
                  type="button"
                  onClick={() =>
                    onChange(selectedValues.filter((v) => v !== id))
                  }
                  className="hover:text-red-500 transition-colors focus:outline-none p-0.5 rounded-full hover:bg-red-50"
                >
                  ✕
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default UserSelectorSearch;
